var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import * as React from 'react';
import { FormField } from '../form';
import { FolderCancelIcon, LinkIcon } from '../icon';
import { Input, InputAdornment } from '../input';
import { Spinner } from '../spinner';
import { useTheme } from '../theme';
import { styled } from '../styled';
var imagePreviewFieldComponentName = 'QuantumImagePreviewField';
// remove all html props it does not need them, and some come from uncontrolled areas
var PreviewWrapper = styled('div', { name: imagePreviewFieldComponentName, slot: 'Preview' })(function (_a) {
    var theme = _a.theme, ownerState = _a.ownerState;
    return ({
        border: "1px solid ".concat(theme.tokens.color_border_input),
        borderRadius: theme.shape.borderRadius,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        borderBottom: 'none',
        backgroundColor: ownerState.disabled ? theme.tokens.color_bg_input_disabled : theme.tokens.color_bg_input,
        height: '120px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    });
});
var ImagePreview = styled('img', { name: imagePreviewFieldComponentName, slot: 'Img' })(function (_a) {
    var ownerState = _a.ownerState;
    return ({
        height: '60px',
        maxWidth: '100%',
        display: ownerState.visible ? 'inline' : 'none',
    });
});
var TextInput = styled(Input)({
    borderTopRightRadius: 0,
    borderTopLeftRadius: 0,
});
export var ImagePreviewFieldState;
(function (ImagePreviewFieldState) {
    ImagePreviewFieldState[ImagePreviewFieldState["Idle"] = 0] = "Idle";
    ImagePreviewFieldState[ImagePreviewFieldState["Loading"] = 1] = "Loading";
    ImagePreviewFieldState[ImagePreviewFieldState["Error"] = 2] = "Error";
})(ImagePreviewFieldState || (ImagePreviewFieldState = {}));
var ImagePreviewContainer = function (props) {
    var value = props.value, defaultValue = props.defaultValue, fallbackValue = props.fallbackValue;
    var _a = __read(React.useState(ImagePreviewFieldState.Idle), 2), state = _a[0], setState = _a[1];
    var theme = useTheme();
    var statusIndicator;
    React.useEffect(function () {
        setState(!value ? ImagePreviewFieldState.Idle : ImagePreviewFieldState.Loading);
    }, [value, defaultValue]);
    if (state === ImagePreviewFieldState.Loading) {
        statusIndicator = React.createElement(Spinner, { size: "medium" });
    }
    if (state === ImagePreviewFieldState.Error) {
        statusIndicator = React.createElement(FolderCancelIcon, { size: 40, color: theme.tokens.color_fg_default });
    }
    return (React.createElement(React.Fragment, null,
        state !== ImagePreviewFieldState.Idle && statusIndicator,
        React.createElement(ImagePreview, { ownerState: { visible: state === ImagePreviewFieldState.Idle }, "data-testid": props['data-testid'] ? "".concat(props['data-testid'], "-image-previewer") : undefined, src: value || defaultValue || fallbackValue, onError: function () { return setState(ImagePreviewFieldState.Error); }, onLoad: function () { return setState(ImagePreviewFieldState.Idle); } })));
};
export var ImagePreviewField = React.forwardRef(function (props, ref) {
    var _a = props.autoFocus, autoFocus = _a === void 0 ? false : _a, defaultValue = props.defaultValue, _b = props.disabled, disabled = _b === void 0 ? false : _b, _c = props.readOnly, readOnly = _c === void 0 ? false : _c, FormHelperTextProps = props.FormHelperTextProps, _d = props.fullWidth, fullWidth = _d === void 0 ? false : _d, hiddenLabel = props.hiddenLabel, InputLabelProps = props.InputLabelProps, inputProps = props.inputProps, InputProps = props.InputProps, inputRef = props.inputRef, label = props.label, name = props.name, onBlur = props.onBlur, onChange = props.onChange, onFocus = props.onFocus, placeholder = props.placeholder, _e = props.required, required = _e === void 0 ? false : _e, value = props.value, 
    // remove props below from being spread to the FormControl
    fallbackValue = props.fallbackValue, 
    // spread the rest to the FormControl at the root
    rootProps = __rest(props, ["autoFocus", "defaultValue", "disabled", "readOnly", "FormHelperTextProps", "fullWidth", "hiddenLabel", "InputLabelProps", "inputProps", "InputProps", "inputRef", "label", "name", "onBlur", "onChange", "onFocus", "placeholder", "required", "value", "fallbackValue"]);
    var isDisabled = disabled || readOnly;
    return (React.createElement(FormField, __assign({ disabled: disabled, readOnly: readOnly, hiddenLabel: hiddenLabel, ref: ref, required: required, label: label, fullWidth: fullWidth, InputLabelProps: InputLabelProps }, rootProps),
        React.createElement(PreviewWrapper, { ownerState: { disabled: isDisabled } },
            React.createElement(ImagePreviewContainer, __assign({}, props))),
        React.createElement(TextInput, __assign({ autoFocus: autoFocus, defaultValue: defaultValue, fullWidth: fullWidth, name: name, value: value, inputRef: inputRef, onBlur: onBlur, onChange: onChange, onFocus: onFocus, placeholder: placeholder, inputProps: inputProps, startAdornment: React.createElement(InputAdornment, { position: "start" },
                React.createElement(LinkIcon, { size: "1.25em" })) }, InputProps))));
});
